import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Play, Eye, ShoppingBag, ExternalLink, X, HelpCircle } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { GraphComments } from "@/components/GraphComments";
import { Footer } from "@/components/Footer";
import { MetaTags } from "@/components/MetaTags";
import { ResponsiveIframe } from "@/components/ResponsiveIframe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOPIFY_STORE_URL = 'https://darkar.co';

// Preroll Skip Button Component
const PrerollSkipButton = ({ skipAfterSeconds, onSkip }) => {
  const [timeRemaining, setTimeRemaining] = useState(skipAfterSeconds);
  const [canSkip, setCanSkip] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          setCanSkip(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (canSkip) {
    return (
      <button
        onClick={onSkip}
        className="flex items-center gap-2 px-4 py-2 bg-white/90 hover:bg-white text-black font-bold text-sm transition-all rounded shadow-lg"
      >
        Saltar anuncio
        <X size={16} />
      </button>
    );
  }

  return (
    <div className="px-4 py-2 bg-black/80 text-white text-sm font-medium rounded">
      Puedes saltar en {timeRemaining}s
    </div>
  );
};

// Preroll Progress Bar Component
const PrerollProgressBar = ({ skipAfterSeconds }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + (100 / skipAfterSeconds);
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [skipAfterSeconds]);

  if (progress >= 100) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
      <div 
        className="h-full bg-[#FFE500] transition-all duration-1000 ease-linear"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Helper to determine contrast color for text
const getContrastColor = (hexColor) => {
  if (!hexColor) return "#000000";
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

// Helper to extract video ID and create embed URL
const getEmbedUrl = (url, platform, autoplay = false) => {
  if (platform === "youtube") {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
      /youtube\.com\/shorts\/([^&\s?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        // YouTube embed parameters:
        // rel=0 - show related videos from same channel only (can't fully disable since 2018)
        // modestbranding=1 - minimal YouTube branding
        // iv_load_policy=3 - no annotations
        // fs=1 - fullscreen enabled
        // controls=1 - show controls
        // disablekb=0 - allow keyboard controls
        const autoplayParam = autoplay ? '&autoplay=1' : '';
        return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1&iv_load_policy=3&fs=1&controls=1&showinfo=0${autoplayParam}`;
      }
    }
  }
  // Note: Vimeo URLs are handled separately by ProtectedVimeoPlayer
  return url;
};

// Extract Vimeo ID from URL
const getVimeoId = (url) => {
  const match = url?.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
};

// Obfuscation helpers - makes it harder to find "vimeo" in DOM
const _0x = {
  p: atob('cGxheWVy'),           // 'player'
  v: atob('dmltZW8='),           // 'vimeo'  
  c: atob('Y29t'),               // 'com'
  d: atob('dmlkZW8='),           // 'video'
};

const buildPlayerUrl = (id, autoplay = false) => {
  // Constructs: https://player.vimeo.com/video/{id}?params
  const autoplayParam = autoplay ? '&autoplay=1' : '';
  const params = `title=0&byline=0&portrait=0&badge=0&dnt=1&autopause=0${autoplayParam}`;
  return `https://${_0x.p}.${_0x.v}.${_0x.c}/${_0x.d}/${id}?${params}`;
};

// Protected Vimeo Player - uses delayed state-based rendering for obfuscation
const ProtectedVimeoPlayer = ({ videoId, title, autoplay = false }) => {
  const [embedUrl, setEmbedUrl] = useState(null);

  useEffect(() => {
    if (!videoId) return;
    
    // Reset URL when videoId changes
    setEmbedUrl(null);
    
    // Delayed URL construction - makes scraping harder
    const timer = setTimeout(() => {
      setEmbedUrl(buildPlayerUrl(videoId, autoplay));
    }, 100);

    return () => clearTimeout(timer);
  }, [videoId, autoplay]);

  if (!embedUrl) {
    return (
      <div className="w-full h-full bg-black flex items-center justify-center">
        <div className="text-white/50 flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          <span className="text-sm">Cargando video...</span>
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={embedUrl}
      title={title || 'Media Player'}
      className="w-full h-full"
      frameBorder="0"
      allow="autoplay; fullscreen; picture-in-picture"
      allowFullScreen
      referrerPolicy="no-referrer"
      loading="lazy"
    />
  );
};

export default function VideoPage() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [season, setSeason] = useState(null);
  const [seasonVideos, setSeasonVideos] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [combinedMetrics, setCombinedMetrics] = useState(null); // Métricas unificadas (cacheadas)
  const [playlistStats, setPlaylistStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [preroll, setPreroll] = useState(null);
  const [showPreroll, setShowPreroll] = useState(false);
  const [prerollCompleted, setPrerollCompleted] = useState(false);
  const [mobileBanner, setMobileBanner] = useState(null);
  const [desktopBanner, setDesktopBanner] = useState(null);
  const [sidebarAd, setSidebarAd] = useState(null);
  const [showViewsInfo, setShowViewsInfo] = useState(false);

  // Find previous and next videos
  // Compare by both id and slug since URL can use either
  const currentIndex = seasonVideos.findIndex(v => v.id === videoId || v.slug === videoId);
  const previousVideo = currentIndex > 0 ? seasonVideos[currentIndex - 1] : null;
  const nextVideo = currentIndex < seasonVideos.length - 1 ? seasonVideos[currentIndex + 1] : null;

  // Fetch active preroll on mount
  useEffect(() => {
    const fetchPreroll = async () => {
      try {
        const res = await axios.get(`${API}/prerolls/active`);
        // Only show preroll if it exists AND has a valid video_url
        if (res.data && res.data.video_url && res.data.video_url.trim()) {
          setPreroll(res.data);
          setShowPreroll(true);
          setPrerollCompleted(false);
        } else {
          // No valid preroll, skip directly to video with autoplay
          setPreroll(null);
          setShowPreroll(false);
          setPrerollCompleted(true);
        }
      } catch (error) {
        // No preroll available - autoplay video directly
        setPreroll(null);
        setShowPreroll(false);
        setPrerollCompleted(true);
      }
    };
    fetchPreroll();
  }, [videoId]); // Reset preroll when video changes

  // Fetch mobile banner
  useEffect(() => {
    const fetchMobileBanner = async () => {
      try {
        const res = await axios.get(`${API}/mobile-banners/active`);
        if (res.data) {
          setMobileBanner(res.data);
        }
      } catch (error) {
        // No banner available
      }
    };
    fetchMobileBanner();
  }, []);

  // Fetch desktop banner
  useEffect(() => {
    const fetchDesktopBanner = async () => {
      try {
        const res = await axios.get(`${API}/desktop-banners/active`);
        if (res.data) {
          setDesktopBanner(res.data);
        }
      } catch (error) {
        // No banner available
      }
    };
    fetchDesktopBanner();
  }, []);

  // Fetch sidebar ad on mount
  useEffect(() => {
    const fetchSidebarAd = async () => {
      try {
        const res = await axios.get(`${API}/sidebar-ads/active`);
        if (res.data) {
          setSidebarAd(res.data);
        }
      } catch (error) {
        // No ad available
      }
    };
    fetchSidebarAd();
  }, []);

  useEffect(() => {
    fetchVideoData();
    // Scroll to top when video page loads
    window.scrollTo(0, 0);
  }, [videoId]);

  useEffect(() => {
    if (video?.id) {
      // Solo usar métricas cacheadas, no llamar a YouTube directamente
      fetchCombinedMetrics();
    }
  }, [video?.id]);

  // Fetch stats for all playlist videos - usar caché del backend
  useEffect(() => {
    const fetchPlaylistStats = async () => {
      const stats = {};
      for (const v of seasonVideos) {
        try {
          // Usar endpoint de métricas que retorna valores cacheados (no consume cuota)
          const res = await axios.get(`${API}/metrics/video/${v.id}`);
          if (res.data?.total_views > 0) {
            stats[v.id] = res.data.total_views;
          }
        } catch (error) {
          // Silently fail
        }
      }
      setPlaylistStats(stats);
    };

    if (seasonVideos.length > 0) {
      fetchPlaylistStats();
    }
  }, [seasonVideos]);

  // Fetch métricas unificadas (vistas combinadas de todas las plataformas) - usa caché
  const fetchCombinedMetrics = async () => {
    try {
      const res = await axios.get(`${API}/metrics/video/${video.id}`);
      setCombinedMetrics(res.data);
    } catch (error) {
      console.error("Error fetching combined metrics:", error);
    }
  };

  const formatViewCount = (count) => {
    if (!count) return null;
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toLocaleString();
  };

  const fetchVideoData = async () => {
    try {
      setLoading(true);
      const videoRes = await axios.get(`${API}/videos/${videoId}`);
      setVideo(videoRes.data);
      
      const [seasonRes, videosRes, shopRes] = await Promise.all([
        axios.get(`${API}/seasons/${videoRes.data.season_id}`),
        axios.get(`${API}/videos?season_id=${videoRes.data.season_id}`),
        axios.get(`${API}/shopify/products?limit=12`).catch(() => ({ data: { products: [] } }))
      ]);
      
      setSeason(seasonRes.data);
      setSeasonVideos(videosRes.data);
      
      // Sort products: available first, sold out at the end
      const sortedProducts = (shopRes.data.products || [])
        .sort((a, b) => {
          const aAllSoldOut = a.variants?.every(v => v.available === false) ?? false;
          const bAllSoldOut = b.variants?.every(v => v.available === false) ?? false;
          if (aAllSoldOut && !bAllSoldOut) return 1;
          if (!aAllSoldOut && bAllSoldOut) return -1;
          return 0;
        })
        .slice(0, 6);
      setShopProducts(sortedProducts);
    } catch (error) {
      console.error("Error fetching video data:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 px-4 md:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="aspect-video skeleton rounded-xl mb-6" />
            <div className="h-8 w-1/2 skeleton rounded mb-4" />
            <div className="h-4 w-3/4 skeleton rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Video no encontrado</p>
      </div>
    );
  }

  // Generate embed URL without autoplay
  const embedUrl = getEmbedUrl(video.video_url, video.platform, false);

  return (
    <div className="min-h-screen flex flex-col" data-testid="video-page">
      <MetaTags 
        title={video.title}
        description={video.description || `Mira ${video.title} en Darkar Company`}
        image={video.thumbnail}
        url={`/video/${video.slug || video.id}`}
        type="video.other"
      />
      <Navbar />
      
      <main className="pt-32 px-4 md:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Header Row - Back Button + Season Badge */}
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(`/?season=${video.season_id}`)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-black uppercase tracking-wide hand-drawn-subtle hover:bg-[#FFE500] transition-colors flex-shrink-0"
              style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
              data-testid="back-btn"
            >
              <ArrowLeft size={20} />
              <span>Atrás</span>
            </button>

            <h2 
              className="flex-1 text-center px-8 py-3 text-xl md:text-2xl font-black uppercase tracking-wide hand-drawn-subtle"
              style={{ 
                backgroundColor: season?.color || '#FFE500',
                color: getContrastColor(season?.color || '#FFE500'),
                borderWidth: '3px', 
                borderColor: getContrastColor(season?.color || '#FFE500'), 
                borderStyle: 'solid' 
              }}
            >
              {season?.title || 'Temporada'}
            </h2>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - Video + Comments */}
            <div className="lg:col-span-2 space-y-8">
              {/* Video Title - Hand drawn style */}
              <div 
                className="p-5 bg-black hand-drawn-subtle"
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
              >
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  {video.title}
                </h1>
              </div>

              {/* Mobile Ad Banner - Only visible on mobile */}
              {mobileBanner && (
                <div className="block lg:hidden">
                  {mobileBanner.ad_type === "html5" && mobileBanner.html_url ? (
                    <a
                      href={mobileBanner.link_url || '#'}
                      target={mobileBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle"
                      style={{ 
                        borderWidth: '3px', 
                        borderColor: '#fff', 
                        borderStyle: 'solid',
                      }}
                    >
                      <ResponsiveIframe
                        src={mobileBanner.html_url}
                        width={600}
                        height={150}
                        title={mobileBanner.title || "Anuncio"}
                      />
                    </a>
                  ) : mobileBanner.ad_type === "video" && mobileBanner.video_url ? (
                    <a 
                      href={mobileBanner.link_url || '#'}
                      target={mobileBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle overflow-hidden"
                      style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    >
                      <video 
                        src={mobileBanner.video_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                      />
                    </a>
                  ) : (
                    <a 
                      href={mobileBanner.link_url || '#'}
                      target={mobileBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle overflow-hidden"
                      style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    >
                      <img 
                        src={mobileBanner.image_url} 
                        alt={mobileBanner.title || "Anuncio"}
                        className="w-full h-auto"
                      />
                    </a>
                  )}
                </div>
              )}

              {/* Desktop Ad Banner - Only visible on desktop */}
              {desktopBanner && (
                <div className="hidden lg:block">
                  {desktopBanner.ad_type === "html5" && desktopBanner.html_url ? (
                    <a
                      href={desktopBanner.link_url || '#'}
                      target={desktopBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle"
                      style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    >
                      <ResponsiveIframe
                        src={desktopBanner.html_url}
                        width={1200}
                        height={150}
                        title={desktopBanner.title || "Anuncio"}
                      />
                    </a>
                  ) : desktopBanner.ad_type === "video" && desktopBanner.video_url ? (
                    <a 
                      href={desktopBanner.link_url || '#'}
                      target={desktopBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle overflow-hidden"
                      style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    >
                      <video 
                        src={desktopBanner.video_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                      />
                    </a>
                  ) : (
                    <a 
                      href={desktopBanner.link_url || '#'}
                      target={desktopBanner.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block hand-drawn-subtle overflow-hidden"
                      style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    >
                      <img 
                        src={desktopBanner.image_url} 
                        alt={desktopBanner.title || "Anuncio"}
                        className="w-full h-auto"
                      />
                    </a>
                  )}
                </div>
              )}

              {/* Video Player - Hand drawn style */}
              <div 
                className="aspect-video bg-black overflow-hidden shadow-2xl hand-drawn-subtle youtube-container relative"
                style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
                data-testid="video-player-container"
              >
                {/* Show Preroll Ad if active and not completed */}
                {showPreroll && preroll && !prerollCompleted ? (
                  <>
                    <iframe
                      src={`https://www.youtube.com/embed/${preroll.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/)?.[1]}?rel=0&modestbranding=1&iv_load_policy=3&fs=1&controls=1&showinfo=0&autoplay=1`}
                      title={preroll.title || "Anuncio"}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                    {/* Preroll overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Ad badge */}
                      <div className="absolute top-3 left-3 px-3 py-1 bg-black/80 text-white text-sm font-bold rounded pointer-events-auto">
                        Anuncio
                      </div>
                      {/* Skip button */}
                      <div className="absolute bottom-4 right-4 pointer-events-auto">
                        <PrerollSkipButton 
                          skipAfterSeconds={preroll.skip_after_seconds || 5}
                          onSkip={() => {
                            setShowPreroll(false);
                            setPrerollCompleted(true);
                          }}
                        />
                      </div>
                      {/* Progress bar */}
                      <PrerollProgressBar skipAfterSeconds={preroll.skip_after_seconds || 5} />
                    </div>
                  </>
                ) : video.platform === 'vimeo' ? (
                  <ProtectedVimeoPlayer 
                    videoId={getVimeoId(video.video_url)} 
                    title={video.title}
                    autoplay={false}
                  />
                ) : (
                  <iframe
                    src={embedUrl}
                    title={video.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    data-testid="video-iframe"
                  />
                )}
              </div>

              {/* Episode Navigation Buttons */}
              <div className="flex gap-4">
                {previousVideo ? (
                  <Link
                    to={`/video/${previousVideo.slug || previousVideo.id}`}
                    className="flex-1 text-center px-6 py-3 font-black uppercase tracking-wide hand-drawn-subtle hover:opacity-80 transition-opacity"
                    style={{ 
                      backgroundColor: season?.color || '#FFE500',
                      color: getContrastColor(season?.color || '#FFE500'),
                      borderWidth: '3px', 
                      borderColor: getContrastColor(season?.color || '#FFE500'), 
                      borderStyle: 'solid' 
                    }}
                    data-testid="prev-video-btn"
                  >
                    &lt; Anterior
                  </Link>
                ) : (
                  <div 
                    className="flex-1 text-center px-6 py-3 bg-gray-600 text-gray-400 font-black uppercase tracking-wide hand-drawn-subtle cursor-not-allowed"
                    style={{ borderWidth: '3px', borderColor: '#555', borderStyle: 'solid' }}
                  >
                    &lt; Anterior
                  </div>
                )}
                {nextVideo ? (
                  <Link
                    to={`/video/${nextVideo.slug || nextVideo.id}`}
                    className="flex-1 text-center px-6 py-3 font-black uppercase tracking-wide hand-drawn-subtle hover:opacity-80 transition-opacity"
                    style={{ 
                      backgroundColor: season?.color || '#FFE500',
                      color: getContrastColor(season?.color || '#FFE500'),
                      borderWidth: '3px', 
                      borderColor: getContrastColor(season?.color || '#FFE500'), 
                      borderStyle: 'solid' 
                    }}
                    data-testid="next-video-btn"
                  >
                    Siguiente &gt;
                  </Link>
                ) : (
                  <div 
                    className="flex-1 text-center px-6 py-3 bg-gray-600 text-gray-400 font-black uppercase tracking-wide hand-drawn-subtle cursor-not-allowed"
                    style={{ borderWidth: '3px', borderColor: '#555', borderStyle: 'solid' }}
                  >
                    Siguiente &gt;
                  </div>
                )}
              </div>

              {/* Video Description - Hand drawn paper style */}
              <div 
                className="p-5 bg-black hand-drawn-subtle"
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
              >
                {video.description && (
                  <p className="text-gray-300 text-base leading-relaxed mb-4">
                    {video.description}
                  </p>
                )}
                <div className="flex items-center justify-end gap-3 text-sm">
                  <Link 
                    to={`/?season=${video.season_id}`}
                    className="px-3 py-1 font-bold uppercase border-2 hover:opacity-80 transition-opacity"
                    style={{
                      backgroundColor: season?.color || '#FFE500',
                      color: getContrastColor(season?.color || '#FFE500'),
                      borderColor: getContrastColor(season?.color || '#FFE500')
                    }}
                  >
                    {season?.title || video.platform}
                  </Link>
                  {/* Mostrar vistas combinadas (video principal + fuentes secundarias) */}
                  {combinedMetrics?.total_views > 0 && (
                    <div className="flex items-center gap-1">
                      <span 
                        className="px-3 py-1 bg-white/10 text-white rounded-full flex items-center gap-1 font-medium whitespace-nowrap"
                        data-testid="video-view-count"
                      >
                        <Eye size={14} />
                        {formatViewCount(combinedMetrics.total_views)} vistas
                      </span>
                      <button
                        onClick={() => setShowViewsInfo(true)}
                        className="w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                        aria-label="Información sobre las vistas"
                        data-testid="views-info-button"
                      >
                        <HelpCircle size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Shop Section - Shopify Products */}
              {shopProducts.length > 0 && (
                <div 
                  className="p-5 bg-black hand-drawn-subtle"
                  style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                >
                  <h2 className="text-lg font-black text-white uppercase mb-4 flex items-center gap-2">
                    <span className="w-1 h-6 bg-[#E91E8C] rounded-full" />
                    Tienda
                  </h2>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {shopProducts.slice(0, 6).map((product) => {
                      const productUrl = `${SHOPIFY_STORE_URL}/products/${product.handle}`;
                      const soldOut = product.variants?.every(v => v.available === false) ?? false;
                      
                      return (
                        <a
                          key={product.id}
                          href={productUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`group block bg-black/50 hand-drawn-subtle overflow-hidden ${
                            soldOut ? 'grayscale opacity-70' : ''
                          }`}
                          style={{ borderWidth: '2px', borderColor: '#fff', borderStyle: 'solid' }}
                        >
                          <div className="relative aspect-square overflow-hidden bg-white">
                            {(product.images?.[0]?.src || product.images?.[0]?.url) ? (
                              <img
                                src={product.images[0].src || product.images[0].url}
                                alt={product.title}
                                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                <ShoppingBag size={24} className="text-white/20" />
                              </div>
                            )}
                            {soldOut && (
                              <div className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold px-1 py-0.5 rounded">
                                AGOTADO
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ExternalLink size={16} className="text-white" />
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* GraphComment Comments */}
              <div 
                className="bg-black hand-drawn-subtle overflow-hidden px-5 pt-4 pb-6"
                style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
              >
                <GraphComments 
                  identifier={video.id}
                  title={video.title}
                  accentColor={season?.color || '#E91E8C'}
                />
              </div>
            </div>

            {/* Sidebar - All Season Episodes */}
            <div className="lg:col-span-1">
              {/* Sidebar Ad - Desktop Only */}
              {sidebarAd && (
                <div className="hidden lg:block mb-6">
                  {sidebarAd.ad_type === "html5" && sidebarAd.html_url ? (
                    /* HTML5 Canvas Banner */
                    <a
                      href={sidebarAd.link_url || '#'}
                      target={sidebarAd.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block rounded-xl overflow-hidden hand-drawn-subtle"
                      style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                      data-testid="sidebar-ad-html5"
                    >
                      <ResponsiveIframe
                        src={sidebarAd.html_url}
                        width={600}
                        height={500}
                        title={sidebarAd.title || "Publicidad"}
                      />
                    </a>
                  ) : sidebarAd.ad_type === "video" && sidebarAd.video_url ? (
                    /* Video Banner */
                    <a 
                      href={sidebarAd.link_url || '#'}
                      target={sidebarAd.link_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className="block rounded-xl overflow-hidden hand-drawn-subtle hover:scale-[1.02] transition-transform"
                      style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                      data-testid="sidebar-ad-video"
                    >
                      <video 
                        src={sidebarAd.video_url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-auto"
                      />
                    </a>
                  ) : sidebarAd.link_url ? (
                    /* Image Banner with Link */
                    <a 
                      href={sidebarAd.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block rounded-xl overflow-hidden hand-drawn-subtle hover:scale-[1.02] transition-transform"
                      style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                      data-testid="sidebar-ad"
                    >
                      <img 
                        src={sidebarAd.image_url}
                        alt={sidebarAd.title || "Publicidad"}
                        className="w-full h-auto"
                      />
                    </a>
                  ) : (
                    /* Image Banner without Link */
                    <div 
                      className="rounded-xl overflow-hidden hand-drawn-subtle"
                      style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                      data-testid="sidebar-ad"
                    >
                      <img 
                        src={sidebarAd.image_url}
                        alt={sidebarAd.title || "Publicidad"}
                        className="w-full h-auto"
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 bg-[#E91E8C] rounded-full" />
                  {season ? `${season.title}` : "Episodios"}
                </h2>
                
                <div className="space-y-4">
                  {seasonVideos.map((v, index) => {
                    const isCurrentVideo = v.id === video.id;
                    return (
                      <Link
                        key={v.id}
                        to={`/video/${v.slug || v.id}`}
                        className={`flex gap-3 p-2 card-jump hand-drawn-subtle group relative ${
                          isCurrentVideo 
                            ? 'bg-[#FFE500] ring-2 ring-[#E91E8C] ring-offset-2' 
                            : 'bg-white hover:bg-gray-50'
                        }`}
                        style={{ borderWidth: '3px', borderColor: '#000', borderStyle: 'solid' }}
                        data-testid={`sidebar-video-${v.id}`}
                      >
                        {/* Thumbnail */}
                        <div 
                          className="relative w-28 aspect-video flex-shrink-0 overflow-hidden bg-gray-200"
                          style={{ borderWidth: '2px', borderColor: '#000', borderStyle: 'solid', borderRadius: '8px 2px 6px 2px/2px 6px 2px 8px' }}
                        >
                          {v.thumbnail ? (
                            <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play size={20} className="text-gray-400" />
                            </div>
                          )}
                          {!isCurrentVideo && (
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <Play size={20} className="text-white fill-white" />
                            </div>
                          )}
                          {isCurrentVideo && (
                            <div className="absolute inset-0 bg-[#E91E8C]/80 flex items-center justify-center">
                              <span className="text-white text-xs font-bold uppercase tracking-wide">Viendo</span>
                            </div>
                          )}
                          {v.duration && !isCurrentVideo && (
                            <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black text-white text-xs font-bold border border-white">
                              {v.duration}
                            </span>
                          )}
                        </div>
                        
                        {/* Video Info */}
                        <div className="flex-1 min-w-0 py-1">
                          <h3 className={`font-bold text-sm line-clamp-2 transition-colors ${
                            isCurrentVideo ? 'text-black' : 'text-black group-hover:text-[#E91E8C]'
                          }`}>
                            {v.title}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            {v.duration && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock size={10} />
                                {v.duration}
                              </span>
                            )}
                            {playlistStats[v.id] && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Eye size={10} />
                                {formatViewCount(playlistStats[v.id])}
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                  
                  {seasonVideos.length === 0 && (
                    <p className="text-gray-500 text-sm py-4">No hay episodios en esta temporada.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <div className="mt-auto">
        <Footer />
      </div>

      {/* Views Info Modal */}
      {showViewsInfo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setShowViewsInfo(false)}
        >
          <div 
            className="bg-[#1a1a1a] border-2 border-white rounded-2xl p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowViewsInfo(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Cerrar"
            >
              <X size={20} />
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#E91E8C]/20 flex items-center justify-center">
                <Eye size={20} className="text-[#E91E8C]" />
              </div>
              <h3 className="text-xl font-bold text-white">¿Cómo se calculan las vistas?</h3>
            </div>
            
            <p className="text-gray-300 mb-4">
              El contador de vistas muestra el <strong className="text-white">total combinado</strong> de reproducciones 
              de este episodio en todas las plataformas donde está disponible.
            </p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </div>
                <span className="text-white">YouTube Data API</span>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
                <span className="text-white">Facebook Graph API</span>
              </div>
            </div>
            
            <p className="text-gray-400 text-sm">
              Las vistas se actualizan automáticamente cada 6 horas para reflejar los números más recientes.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
